import RouteWaypoints, { getRoutePathPct } from "./RouteWaypoints";
import RouteHiker from "./RouteHiker";
import { Reveal, StaggerGroup, getStaggerChild } from "./motion";
import { InViewTextEffect } from "@/components/ui/text-effect";
import { motion, useReducedMotion } from "framer-motion";

const BADGES = ["Army Veteran", "Colorado-Based", "Solo-Operated"];
const FOUNDER_IMG = "/founder-placeholder.jpg";

const ELEVATION_POINTS = [
  [0.05, 0.78], [0.25, 0.68], [0.45, 0.42], [0.65, 0.3], [0.95, 0.26],
];
const ELEVATION_PATH = getRoutePathPct(ELEVATION_POINTS);

export default function About({ hikerVisible = false }) {
  const reduce = useReducedMotion();
  const staggerChild = getStaggerChild(reduce);
  return (
    <section
      id="about"
      className="relative z-10 px-5 py-24 md:px-8 md:py-32"
      data-testid="about-section"
    >
      <div className="mx-auto max-w-7xl">
        <InViewTextEffect
          as="p"
          preset="blur"
          className="font-mono text-[11px] uppercase tracking-[0.24em] text-rust"
        >
          CH.01 // The Operator
        </InViewTextEffect>

        <div className="mt-10 grid grid-cols-1 gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">
          {/* Left — story */}
          <div>
            <Reveal
              as="h2"
              className="max-w-xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl"
            >
              A mechanic's hands, a soldier's <em className="italic text-pine">plan</em>,
              a local's map.
            </Reveal>

            <div className="mt-8 max-w-xl space-y-5 text-base leading-relaxed text-ink-dim md:text-lg">
              <Reveal delay={0.05}>
                <p>
                  I'm Jamie Zigler — Army veteran, longtime mechanic, and Colorado
                  local, based in Peyton and building for clients across Colorado
                  Springs, Denver, and the Front Range. In the service I learned
                  that a good outcome starts with a plan you can actually read:
                  known terrain, clear objective, no guessing. I build websites
                  the same way. We diagnose what your business needs, map the
                  route, and I own the execution end to end.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <p>
                  Every site is built for the person who actually uses it. Bedrock
                  needed contractors to trust them and request a quote from a phone.
                  That's what we built.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p>
                  I take exactly one client build a month — on purpose. It's not a
                  limitation, it's how the work stays sharp. One project means full
                  attention, same-day answers, and no queue you're stuck behind.
                  When the slot's yours, it's yours.
                </p>
              </Reveal>
            </div>

            {/* identity badges */}
            <StaggerGroup className="mt-9 flex flex-wrap gap-3" delay={0.1}>
              {BADGES.map((b) => (
                <motion.span
                  key={b}
                  variants={staggerChild}
                  data-testid={`about-badge-${b.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-hairline bg-panel/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-ink"
                >
                  {b}
                </motion.span>
              ))}
            </StaggerGroup>

            {/* founder portrait — clipped frame */}
            <Reveal delay={0.15} className="mt-12 max-w-sm">
              <div className="relative overflow-hidden border border-hairline bg-panel">
                <img
                  src={FOUNDER_IMG}
                  alt="A Colorado beaver flying the state flag — unofficial mascot of Zigler Designs"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover object-center grayscale-[0.35] contrast-[1.05]"
                  data-testid="about-founder-photo"
                />
                <div className="flex items-center justify-between border-t border-hairline bg-paper px-4 py-2.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink">
                    Zigler Designs
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-dim">
                    // Unofficial Mascot
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right — elevation profile panel */}
          <Reveal delay={0.1} className="lg:pt-16">
            <div className="border border-hairline bg-panel/60 p-6 md:p-8">
              <div className="flex items-center justify-between border-b border-hairline pb-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink">
                  Elevation Profile
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-dim">
                  ZD // Home Range
                </span>
              </div>
              <div className="py-6">
                <div className="relative">
                  <RouteWaypoints
                    viewBox="0 0 560 220"
                    points={ELEVATION_POINTS}
                    waypoints={[{ at: 3, label: "6,800 FT", labelAbove: true }]}
                  />
                  {hikerVisible && <RouteHiker path={ELEVATION_PATH} size={38} speed={30} />}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-px border-t border-hairline pt-4 text-center">
                {[
                  ["Datum", "NAVD 88"],
                  ["Grade", "Surveyed"],
                  ["Peak", "6,800 FT"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-dim">
                      {k}
                    </p>
                    <p className="mt-1.5 font-mono text-xs text-ink">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
